function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { Helpers, useLoanStatusQuery, useTrackingItemsQuery } from '../../../hooks';
import React, { createContext, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Divider, HStack, Hidden, ScrollView, SectionList, Stack, Text, VStack } from 'native-base';
import { DocUploadSkeleton, ESignWrapper, ListHeaderComponent, TrackingItem as TrackingItemRow, UploadBox } from '../../../components';
import StatusTrackerContainer from '../StatusTracker';
import LoanOverviewContainer from '../LoanOverview';
import { useQueryClient } from '@tanstack/react-query';
import MessageRow from '../../../components/MessageRow';
import { ContextProvider } from '../../../utils';
import AvatarIcon from '../../../components/AvatarIcon';
import PlusOutlineIcon from '../../../assets/icons/PlusOutline';
import get from 'lodash.get';
import IconButton from '../../../components/IconButton';
import Congratulations from '../../../components/NewLoanInProgress/Congratulations';
export const UserDetailsContext = /*#__PURE__*/createContext({
  url: '',
  loanNumber: '',
  authToken: ''
});
const TimeStatus = {
  'Pending Review': {
    value: 'Last Updated',
    color: 'black.700'
  },
  'Approved': {
    value: 'Approved',
    color: 'green.400'
  },
  'Upload History': {
    value: 'Uploaded on',
    color: 'black.700'
  }
};
const TrackingItemsContainer = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    url,
    loanNumber,
    authToken,
    logApiEvent,
    logUserEvent,
    onBrowse,
    onTakePhoto,
    widgets,
    setConfettiExplosion,
    userName,
    onPreview,
    handleOnTokenResponse,
    selfServUrl,
    xApiKey
  } = _ref;
  const {
    data: status,
    isInitialLoading: isTrackerLoading
  } = useLoanStatusQuery({
    accessToken: authToken,
    url,
    logApiEvent,
    loanNumber,
    enabled: widgets.indexOf('status_tracker_widget') >= 0,
    select: data => {
      return get(data, ['responseDetails', 'status', 'statusCode'], 0);
    }
  });
  const {
    data,
    isInitialLoading
  } = useTrackingItemsQuery({
    url,
    accessToken: authToken,
    loanNumber,
    logApiEvent,
    select: response => Helpers.transformToSections(response),
    enabled: widgets.indexOf('doc_upload_widget') >= 0 && !isTrackerLoading
  });
  const flatListRef = useRef();
  const [selected, setSelected] = useState('');
  const [subItemSelected, setSubItemSelected] = useState('');
  const [invalidateCache, setInvalidateCache] = useState(false);
  const client = useQueryClient();
  const [borderColor, setBorderColor] = useState('border.light');
  const invalidateCacheOnSuccess = useCallback(async () => {
    await client.invalidateQueries({
      queryKey: ['tracking-items', loanNumber]
    });
    setInvalidateCache(false);
  }, [client, loanNumber]);
  useEffect(() => {
    if (invalidateCache && selected === '') {
      invalidateCacheOnSuccess();
    }
  }, [invalidateCache, invalidateCacheOnSuccess, selected]);
  const renderItem = useCallback(_ref2 => {
    let {
      item
    } = _ref2;
    return /*#__PURE__*/React.createElement(TrackingItemRow, {
      status: item.status,
      data: item.data || undefined,
      key: `${item.name}-${item.itemIndex}`,
      title: item.name || item.title,
      detail: item.details,
      itemIndex: item.itemIndex,
      setSelected: async id => {
        setSelected(id);
        setSubItemSelected('');
      },
      isDisabled: !!(selected && selected !== `${item.name || item.title}-${item.itemIndex}`),
      opacity: selected && selected !== `${item.name || item.title}-${item.itemIndex}` ? 50 : 100,
      sectionIndex: item.sectionIndex,
      infoText: item.infoText,
      color: item.color,
      render: function (statusList, title, sectionIndex, detail) {
        let isMobile = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
        if (item.trackingItemName === 'Disclosures') {
          return /*#__PURE__*/React.createElement(Box, {
            ml: {
              base: 20
            }
          }, /*#__PURE__*/React.createElement(VStack, {
            space: 3
          }, /*#__PURE__*/React.createElement(Text, {
            fontSize: "sm",
            color: "black.700",
            accessibilityLabel: detail,
            testID: detail,
            display: {
              base: 'flex',
              md: 'none',
              lg: 'none'
            }
          }, detail), isMobile ? /*#__PURE__*/React.createElement(MessageRow, {
            text: item.infoText,
            color: item.color,
            opacity: 100,
            maxW: '85%'
          }) : null));
        }
        return sectionIndex === 0 ? /*#__PURE__*/React.createElement(UploadBox, {
          trackingItemName: item.trackingItemName,
          sectionIndex: sectionIndex,
          detail: detail,
          logUserEvent: logUserEvent,
          infoText: item.infoText,
          color: item.color
        }) : /*#__PURE__*/React.createElement(AddMoreItems, {
          data: statusList,
          title: title,
          onSelected: some => {
            setSubItemSelected(some);
          },
          selected: subItemSelected,
          sectionIndex: sectionIndex,
          logUserEvent: logUserEvent
        });
      },
      logUserEvent: logUserEvent
    });
  }, [logUserEvent, selected, subItemSelected]);
  if (isInitialLoading) return /*#__PURE__*/React.createElement(DocUploadSkeleton, null);
  return !data || status === 74 ? /*#__PURE__*/React.createElement(ScrollView, {
    flex: 1
  }, /*#__PURE__*/React.createElement(Stack, {
    flexDirection: {
      lg: 'row'
    },
    p: "4",
    space: 4
  }, /*#__PURE__*/React.createElement(Box, {
    flex: {
      lg: 2
    },
    mr: {
      lg: 10
    }
  }, /*#__PURE__*/React.createElement(StatusTrackerContainer, {
    key: "status-tracker-container",
    loanNumber: loanNumber,
    url: url,
    accessToken: authToken,
    logApiEvent: logApiEvent,
    isEnabled: widgets.indexOf('status_tracker_widget') >= 0 || false
  })), /*#__PURE__*/React.createElement(Box, {
    flex: {
      lg: 1
    }
  }, /*#__PURE__*/React.createElement(LoanOverviewContainer, {
    key: "loan-overview-container",
    loanNumber: loanNumber,
    url: url,
    accessToken: authToken,
    logApiEvent: logApiEvent,
    isEnabled: widgets.indexOf('status_tracker_widget') >= 0 || false,
    statusCode: status
  }))), status === 74 ? /*#__PURE__*/React.createElement(Box, {
    m: "4"
  }, /*#__PURE__*/React.createElement(Congratulations, {
    key: "congratulations-container",
    title: `Congratulations${userName ? ', ' + userName + ',' : ''} on closing your loan!`,
    description: "It was our pleasure helping you achieve your home financial goals.",
    setConfettiExplosion: setConfettiExplosion
  })) : null) : /*#__PURE__*/React.createElement(UserDetailsContext.Provider, {
    value: {
      url,
      loanNumber,
      authToken,
      logApiEvent,
      onBrowse,
      onTakePhoto,
      onPreview,
      setInvalidateCache,
      borderColor,
      setBorderColor,
      status
    }
  }, /*#__PURE__*/React.createElement(SectionList, {
    ref: flatListRef,
    sections: data,
    renderItem: renderItem,
    renderSectionHeader: _ref3 => {
      let {
        //@ts-ignore
        section: {
          title,
          description,
          sectionIndex
        }
      } = _ref3;
      return /*#__PURE__*/React.createElement(React.Fragment, null, sectionIndex === 0 ? /*#__PURE__*/React.createElement(Stack, {
        space: {
          base: 6,
          md: 8,
          lg: 8
        },
        direction: {
          base: 'column',
          md: 'column',
          lg: 'row'
        },
        key: `${sectionIndex}-${title}`
      }, /*#__PURE__*/React.createElement(Box, {
        flex: {
          base: 1,
          md: 1,
          lg: 2
        }
      }, /*#__PURE__*/React.createElement(StatusTrackerContainer, {
        loanNumber: loanNumber,
        url: url,
        accessToken: authToken,
        logApiEvent: logApiEvent,
        isEnabled: widgets.indexOf('status_tracker_widget') >= 0 || false
      })), /*#__PURE__*/React.createElement(Box, {
        flex: {
          base: undefined,
          md: undefined,
          lg: 1
        }
      }, /*#__PURE__*/React.createElement(LoanOverviewContainer, {
        loanNumber: loanNumber,
        url: url,
        accessToken: authToken,
        logApiEvent: logApiEvent,
        isEnabled: widgets.indexOf('status_tracker_widget') >= 0 || false,
        statusCode: status
      }))) : null, /*#__PURE__*/React.createElement(ListHeaderComponent, {
        title,
        description,
        selected: selected,
        sectionIndex: sectionIndex
      }), sectionIndex === 0 ? /*#__PURE__*/React.createElement(ESignWrapper, {
        logApiEvent: logApiEvent,
        logUserEvent: logUserEvent,
        loanNumber: loanNumber,
        url: selfServUrl,
        selected: selected,
        authToken: authToken,
        handleOnTokenResponse: handleOnTokenResponse,
        ref: ref,
        isEnabled: widgets.indexOf('disclosure_widget') >= 0 || false,
        xApiKey: xApiKey
      }) : null);
    },
    keyExtractor: (item, index) => {
      return `${item.itemIndex}-${index}`;
    },
    extraData: selected,
    stickySectionHeadersEnabled: false,
    contentContainerStyle: {
      padding: 16
    }
  }));
});
const TrackingItems = /*#__PURE__*/forwardRef((props, ref) => {
  return /*#__PURE__*/React.createElement(ContextProvider, null, /*#__PURE__*/React.createElement(TrackingItemsContainer, _extends({}, props, {
    ref: ref
  })));
});
export default TrackingItems;
const AddMoreItems = _ref4 => {
  let {
    data,
    title,
    selected,
    onSelected,
    sectionIndex,
    logUserEvent
  } = _ref4;
  useEffect(() => {
    if (Array.isArray(data) && data.length) data.map(each => {
      logUserEvent({
        action: `${each.name.split(' ').join('')}Displayed`,
        type: 'NewLoanInProgress'
      });
    });
  }, [data, logUserEvent, title]);
  return data.map((each, index) => {
    return /*#__PURE__*/React.createElement(VStack, {
      key: each.name,
      space: "4",
      m: "4"
    }, /*#__PURE__*/React.createElement(HStack, {
      alignItems: {
        base: 'flex-start',
        md: 'center',
        lg: 'center'
      },
      justifyContent: "space-between"
    }, /*#__PURE__*/React.createElement(Box, {
      opacity: selected ? 50 : 100
    }, /*#__PURE__*/React.createElement(AvatarIcon, {
      name: each.name,
      isOnlyIcon: true
    })), /*#__PURE__*/React.createElement(Stack, {
      flexDirection: {
        base: 'column',
        md: 'row',
        lg: 'row'
      },
      flex: 1,
      alignItems: {
        md: 'center',
        lg: 'center',
        base: 'flex-start'
      },
      ml: 9,
      space: {
        base: 4,
        lg: 0,
        md: 0
      }
    }, /*#__PURE__*/React.createElement(Text, {
      opacity: selected ? 50 : 100,
      flex: 1,
      fontSize: "sm",
      lineHeight: "2xs",
      fontWeight: "medium",
      fontFamily: "body",
      color: "text.running",
      accessibilityLabel: each.name,
      testID: each.name
    }, each.name), title === 'Upload History' ? /*#__PURE__*/React.createElement(Text, {
      opacity: selected ? 50 : 100,
      flex: 1,
      fontSize: "sm",
      lineHeight: "2xs",
      fontWeight: "normal",
      fontFamily: "body",
      color: get(TimeStatus, [title, 'color'], 'black.700'),
      accessibilityLabel: each.documentName,
      testID: each.documentName
    }, each.documentName) : null, /*#__PURE__*/React.createElement(Text, {
      opacity: selected ? 50 : 100,
      flex: 2,
      fontSize: "sm",
      lineHeight: "2xs",
      fontWeight: "normal",
      fontFamily: "body",
      color: get(TimeStatus, [title, 'color'], 'black.700'),
      accessibilityLabel: each.name,
      testID: each.name
    }, get(TimeStatus, [title, 'value'], ''), ":", ' ', each.trackingDate.replace(':', ' at '), " ET"), /*#__PURE__*/React.createElement(Hidden, {
      till: "sm"
    }, title === 'Pending Review' ? /*#__PURE__*/React.createElement(IconButton, {
      isDisabled: !!selected,
      _disabled: {
        bg: '#425364'
      },
      title: 'Add More',
      Icon: /*#__PURE__*/React.createElement(PlusOutlineIcon, null),
      alignSelf: {
        md: 'flex-end',
        lg: 'flex-end',
        base: 'flex-start'
      },
      onPress: () => {
        logUserEvent({
          action: `${each.name}AddRowClicked`,
          type: 'NewLoanInProgress'
        });
        onSelected(`${each.name}-${index}`);
      }
    }) : null))), /*#__PURE__*/React.createElement(Hidden, {
      from: "sm"
    }, title === 'Pending Review' ? /*#__PURE__*/React.createElement(IconButton, {
      isDisabled: !!selected,
      _disabled: {
        bg: '#425364'
      },
      title: 'Add More',
      Icon: /*#__PURE__*/React.createElement(PlusOutlineIcon, null),
      alignSelf: {
        md: 'flex-end',
        lg: 'flex-end',
        base: 'flex-start'
      },
      onPress: () => {
        logUserEvent({
          action: `${each.name}AddRowClicked`,
          type: 'NewLoanInProgress'
        });
        onSelected(`${each.name}-${index}`);
      },
      w: {
        base: 'full',
        md: 202,
        lg: 202
      }
    }) : null), selected === `${each.name}-${index}` ? /*#__PURE__*/React.createElement(UploadBox, {
      trackingItemName: each.trackingItemName,
      sectionIndex: sectionIndex,
      detail: undefined,
      logUserEvent: logUserEvent,
      infoText: undefined,
      color: undefined
    }) : null, data.length === index + 1 ? null : /*#__PURE__*/React.createElement(Divider, null));
  });
};
//# sourceMappingURL=index.js.map